import React, { useEffect, useState, ChangeEvent, useCallback } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../utils/adminDashboardUtils';
import { Slide, Alert, Snackbar, Button, Card, Select, FormControl, MenuItem, SelectChangeEvent } from '@mui/material';
import { RemediationValidationAdminRow } from '../../interfaces/questionnaire.interface';
import { fetchBestPractices, isWorkflowFiveThreeOneOrGreater } from '../../services/questionnaires';
import { createAssessmentNotification, getRemediationValidationAdminData, handleSaveSubRemediations, updateAssessmentSingleKey } from '../../services/assessments';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ThumbUp } from '@mui/icons-material';
import { fetchAll } from '../../app/store/questionnaireSlice';
import { AssessorAnswer, BPVerification } from '../../interfaces/assessment.interface';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import Moment from 'moment/moment';
import Done from '@mui/icons-material/Done';
import { addBPVerification, deleteBPVerification } from '../../services/assessments';
import { BestPractice } from '../../interfaces/bestPractice.interface';
import debounce from 'lodash/debounce';
import getAuthSession from '../../services/auth';
import axios from 'axios';
import BestPracticeVerificationDialog from './BestPracticeVerificationDialog';
import VerificationFlowDialog from './Modals/VerificationFlowDialog';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { green, grey } from '@mui/material/colors';
import { fetchTPNAdmins } from '../../services/user';
import { setShowRemediationValidationAlert, setShowTPNAdminAssignedAlert } from '../../app/store/alertSlice';
import { GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, eligibleForGoldStarBadge, eligibleForNextGenGoldBadge, processVerificationAward } from '../../services/badge';
import VerificationActionsCell from './VerificationActionsCell';
import { REMEDIATION_OPTIONS, REMEDIATION_VALIDATION_STATUS_UPDATE, REMEDIATION_VERIFICATION_STATUS, VerificationStatus } from '../../services/surveyHelpers';
import { createAuditLog } from '../../services/commentsLogs';
import { LABELS } from '../../utils/labels';
import { Answer } from '../../interfaces/answer.interface';
import { Badge } from '../../interfaces/users.interface';
import BaseToolbar from '../Datagrid/BaseToolbar';
import { getVerificationRemediationsByWorkflow } from '../../services/workflow';

function CustomToolbar({
  openFilterModal,
  filterModelDraft,
  setFilterModelDraft,
  handleFetchVerificationData,
  ...toolbarProps
}: {
  openFilterModal: () => void;
  filterModelDraft: GridFilterModel;
  setFilterModelDraft: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  handleFetchVerificationData: (skipLoader?: boolean, resetPage?: boolean, filters?: GridFilterModel) => void;
  [k: string]: any;
}) {
  const hasActiveFilters = filterModelDraft.items.some(
    (item) => item.value !== undefined && item.value !== '',
  );

  const handleClearFilters = () => {
    const emptyFilters = { items: [] };
    setFilterModelDraft(emptyFilters);
    handleFetchVerificationData(false, true, emptyFilters);
  };

  return (
    <BaseToolbar {...toolbarProps}>
      <Button
        variant="text"
        size="small"
        startIcon={<PlaylistAddCheckIcon />}
        onClick={openFilterModal}
      >
        Validation Best Practices
      </Button>

      {hasActiveFilters && (
        <>
          <Button
            variant="text"
            size="small"
            onClick={() => handleFetchVerificationData(false, true, filterModelDraft)}
          >
            Apply Filters
          </Button>
          <Button variant="text" size="small" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </>
      )}
    </BaseToolbar>
  );
}

export default function RemediationVerificationAdminPanel() {
  const dispatch = useAppDispatch();
  const { showRemediationValidationAlert, showTPNAdminAssignedAlert } = useAppSelector((state) => state.alertSlice);
  const { user } = useAppSelector((state) => state.user);
  const [disableAdminList, setDisableAdminList] = useState<Record<string, number | null>>({});

  const [rows, setRows] = useState<RemediationValidationAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingBP, setLoadingBP] = useState(false);
  const [questionnaireId, setQuestionnaireId] = useState('');
  const questionnaires = useAppSelector(state => state.questionnaires.results);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [openVerificationFlowDialog, setOpenVerificationFlowDialog] = useState(false);
  const { bpVerificationList } = useAppSelector((state) => state.assessments);
  const [searchTerm, setSearchTerm] = useState('');
  const [JWToken, setJWToken] = useState('');
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [controlList, setControlList] = useState<BPVerification[]>([]);
  const [bestPracticeList, setBestPracticeList] = useState<BestPractice[]>([]);
  const [autocompleteValue, setAutocompleteValue] = useState<BestPractice | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [tpnAdmins, setTPNAdmins] = useState<any[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<RemediationValidationAdminRow | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [rowCount, setRowCount] = useState(0);
  const [filterModelDraft, setFilterModelDraft] = React.useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = React.useState<GridFilterModel>({ items: [] });

  const handleActionClick = useCallback( async (rowData: RemediationValidationAdminRow) => {
    try {
      const detailedVerificationData = await getVerificationRemediationsByWorkflow(rowData.id);
      const row = detailedVerificationData.results.find(item => item.id === rowData.id) || null;

      if (!row?.assessment) {
        console.error('Assessment data is missing for the selected row.');
        return;
      }

      const mergedRowData = {
        ...rowData,
        remediationQuestionAnswers: row?.remediationQuestionAnswers || [],
        assessment: row.assessment,
      };
      setSelectedRowData(mergedRowData);
    } catch (error) {
      console.error('Error fetching verification remediations:', error);
    } finally {
      setOpenVerificationFlowDialog(true);
    }
  }, []);

  const handleUpdateAssessmentTPNAdmin = useCallback(async (assessmentId: number, tpnAdminId: string) => {
    try {
      setDisableAdminList({ id: assessmentId });
      await updateAssessmentSingleKey(assessmentId, 'assignedTpnAdminReviewer', tpnAdminId);
      dispatch(setShowTPNAdminAssignedAlert(true));
      const assignedTpnAdminReviewer = tpnAdmins.find(admin => admin.id === tpnAdminId) || null;
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.assessment?.id === assessmentId
            ? { ...row, assignedTpnAdminReviewer }
            : row,
        ),
      );
    } catch (error) {
      console.error('Error updating TPN Admin:', error);
    } finally {
      setDisableAdminList({ id: null });
    }
  }, [tpnAdmins, dispatch]);

  const fetchTPNAdminsList = async () => {
    try {
      const { results } = await fetchTPNAdmins();
      setTPNAdmins(results || []);
    } catch (error) {
      console.error('Error fetching TPN Admins:', error);
    }
  };

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'newAssessment',
      headerName: 'New\nAssessment',
      width: 125,
      filterable: false,
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <CheckCircleIcon sx={{ color: green[500] }} />
        ) : (
          <CancelIcon sx={{ color: grey[500] }} />
        ),
    },
    { field: 'serviceProviderName', filterOperators: stringContainsOnly, headerName: 'Company', width: 200 },
    { field: 'siteOrAppName', filterOperators: stringContainsOnly, headerName: 'Site/Application', width: 200 },
    {
      field: 'questionnaireVersion',
      headerName: 'Questionnaire',
      filterOperators: stringContainsOnly,
      width: 125,
      valueGetter: (params) => `v${params.row.questionnaireVersion}`,
      valueFormatter: (params) => params.value || 'Unknown',
    },
    {
      field: 'serviceProviderAdmin',
      headerName: 'SP Admin',
      filterOperators: stringContainsOnly,
      width: 125,
      valueGetter: (params) => params.row.serviceProviderAdmin,
      valueFormatter: (params) => params.value || 'Unknown',
    },
    {
      field: 'latestControlsUpdatedAt',
      headerName: 'Latest Question\nUpdated On',
      width: 150,
      valueGetter: (params) => params.row.latestControlsUpdatedAt,
      valueFormatter: (params) =>
        params.value ? Moment(params.value).format('MM/DD/YYYY') : '',
    },
    {
      field: 'assignedTpnAdminReviewer',
      headerName: 'TPN Admin Assigned',
      width: 220,
      editable: false,
      sortComparator: (v1, v2) => {
        return v1?.firstName.localeCompare(v2?.firstName);
      },
      renderCell: (params) => {
        const adminOptions = tpnAdmins;
        const handleChange = async (event: SelectChangeEvent) => {
          const selectedAdmin = JSON.parse(event.target.value);
          handleUpdateAssessmentTPNAdmin(params.row.assessment.id, selectedAdmin.id);
        };
        const selectedAdmin = adminOptions.find(
          (admin) => params.value && admin.id === params.value.id,
        );

        return (
          <FormControl size="small" fullWidth>
            <Select
              disabled={disableAdminList.id === params.row.assessment.id}
              value={selectedAdmin ? JSON.stringify(selectedAdmin) : ''}
              onChange={handleChange}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="">Unassigned</MenuItem>
              {adminOptions.map((admin) => (
                <MenuItem key={admin.id} value={JSON.stringify(admin)}>
                  {`${admin.firstName} ${admin.lastName} — ${admin.email}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'questionsToValidateCount',
      headerName: 'Questions \nto Validate',
      filterOperators: stringContainsOnly,
      width: 125,
      valueFormatter: (params) => params.value,
      sortComparator: (v1, v2) => v1 - v2,
    },
    {
      field: 'subControlsWithEvidenceCount',
      headerName: 'Sub-Controls \nwith Evidence',
      filterOperators: stringContainsOnly,
      width: 125,
      valueFormatter: (params) => params.value || 0,
      sortComparator: (v1, v2) => v1 - v2,
    },
    {
      field: 'subControlsRemediationPlanWillRemediateLaterCount',
      headerName: 'Sub-Controls\nRemediate Later',
      filterOperators: stringContainsOnly,
      width: 125,
      valueFormatter: (params) => params.value || 0,
      sortComparator: (v1, v2) => v1 - v2,
    },
    {
      field: 'subControlsRejectedCount',
      headerName: 'Sub-Controls\nRejected',
      filterOperators: stringContainsOnly,
      width: 125,
      valueFormatter: (params) => params.value || 0,
      sortComparator: (v1, v2) => v1 - v2,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      filterable: false,
      width: 125,
      sortable: false,
      renderCell: (params) => <VerificationActionsCell handleActionClick={handleActionClick} row={params.row} />,
    },
  ];

  function buildFilterObject(model: GridFilterModel): Record<string, string | number> {
    return model.items.reduce((acc, filter) => {
      if (filter.value !== undefined && filter.value !== '') {
        acc[filter.columnField] = filter.value as string | number;
      }
      return acc;
    }, {} as Record<string, string | number>);
  }

  const handleFetchVerificationData = React.useCallback(
    async (
      skipLoader = false,
      resetPage = false,
      filtersOverride?: GridFilterModel,
      pageOverride?: number,
    ) => {
      if (!skipLoader) setLoading(true);

      const page = resetPage ? 0 : pageOverride ?? paginationModel.page;
      const offset = page * paginationModel.pageSize;

      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }

      const limit = paginationModel.pageSize;
      const filters = buildFilterObject(filtersOverride || filterModelApplied);

      try {
        const { results, count } = await getRemediationValidationAdminData(offset, limit, filters, sortModel);
        setRowCount(count || 0);
        setRows(results);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching verification data:', error);
        setLoading(false);
      }
    },
    [paginationModel.page, paginationModel.pageSize, filterModelApplied, sortModel],
  );

  const handleFetchBestPracticeQuestions = React.useCallback(async (questionnaire_id: string) => {
    setQuestionnaireId(questionnaire_id);
    if (!bpVerificationList) return;
    setLoadingBP(true);
    const { results } = await fetchBestPractices(undefined, { questionnaire_id });
    const filteredList = bpVerificationList
      ? bpVerificationList
        .filter(({ bestpracticeId }) => results.find((q: BestPractice) => q.id == bestpracticeId))
      : [];
    setControlList(filteredList);
    setLoadingBP(false);
  }, [bpVerificationList]);

  const handleOpenFilterModal = () => {
    setSearchTerm('');
    setOpenFilterModal(true);
  };

  const handleBestPracticeChange = async (value: BestPractice | null) => {
    if (!value || controlList.find((control) => control.bestpracticeId === value.id)) {
      setAutocompleteValue(null);
      return;
    }

    try {
      const data = await addBPVerification(value.id);
      const newBPVerification = {
        id: data.id,
        bestpracticeId: value.id,
        title: value.title,
      };
      setControlList([...controlList, newBPVerification]);
      setShowSnackBar(true);
      setAutocompleteValue(null);
    } catch (error) {
      alert('Error adding Best Practice Validation');
      console.error('Error adding Best Practice Validation:', error);
    }
  };

  const fetchItems = async (value: string) => {
    if (isFetching) return;

    setIsFetching(true);

    if (!questionnaireId || !JWToken) {
      setIsFetching(false);
      return;
    }

    if (!value.trim()) {
      setBestPracticeList([]);
      setIsFetching(false);
      return;
    }

    try {
      const url = `${process.env.REACT_APP_BASE_API}/best-practices/?search=${value}&questionnaire_id=${questionnaireId}&limit=9999`;
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${JWToken}` },
      });
      setBestPracticeList(data.results);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = debounce(async (event: ChangeEvent<{ value: unknown }>) => {
    const inputValue = event.target.value as string;
    if (inputValue.trim().length >= 1) {
      await fetchItems(inputValue);
    } else {
      setBestPracticeList([]);
    }
  }, 500);

  const handleDeleteBPVerification = async (id: string) => {
    try {
      await deleteBPVerification(parseInt(id));
      const updatedList = controlList.filter((control) => control.id !== id);
      setControlList(updatedList);
      setShowSnackBar(true);
    } catch (error) {
      alert('Error deleting Best Practice Validation');
      console.error('Error deleting Best Practice Validation:', error);
    }
  };

  const handleSaveVerificationStatus = useCallback(async (id: string, verificationStatus: VerificationStatus | null, verificationComment: string) => {
    if (!selectedRowData || !user || !selectedRowData.remediationQuestionAnswers) return;

    const currentSubRemediation = selectedRowData.remediationQuestionAnswers.find((item) => item.id && item.id == parseInt(id));
    if (!currentSubRemediation) {
      console.error('Current sub-remediation not found:', id);
      return;
    }

    const subRemediation = {
      id: currentSubRemediation.id,
      verificationStatus,
      verificationComment,
      verificationDate: new Date().toISOString(),
      verifiedOn: new Date().toISOString(),
      verifiedBy: user.id,
    };

    const originalVerificationComment = currentSubRemediation.verificationComment || '';
    const originalVerificationStatus = currentSubRemediation.verificationStatus || '';
    const method = !originalVerificationComment ? 'post' : 'patch';
    const description = !originalVerificationComment
      ? `Status: "${verificationStatus}"\nComment: "${verificationComment}"`
      : `Status changed from "${originalVerificationStatus}" to "${verificationStatus}"\nComment changed from "${originalVerificationComment}" to "${verificationComment}".`;

    try {
      const data = await handleSaveSubRemediations([subRemediation]);
      if (!data || data.length === 0) {
        console.error('No data returned from handleSaveSubRemediations');
        return;
      }

      const updatedSubRemediation = data[0];
      const currentSelectedRow = rows.find((row) => row.id === selectedRowData.id);

      if (!currentSelectedRow) return;

      const updateSelectedRow = currentSelectedRow ? {
        ...currentSelectedRow,
        remediationQuestionAnswers: selectedRowData.remediationQuestionAnswers?.map((item) =>
          item.id === updatedSubRemediation.id ? updatedSubRemediation : item,
        ),
      } : null;

      setSelectedRowData(updateSelectedRow);
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === selectedRowData.id ? updateSelectedRow! : row,
        ),
      );

      // Check badge eligibility
      if (verificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed && isWorkflowFiveThreeOneOrGreater(selectedRowData.workflowType)) {
        const nextGenEligible = await eligibleForNextGenGoldBadge(selectedRowData.id);
        if (nextGenEligible) {
          await processVerificationAward(GOLD_NEXT_GEN_BADGE_LABEL, selectedRowData.id);
        }
        const goldStarEligible = await eligibleForGoldStarBadge(selectedRowData.id);
        if (goldStarEligible) {
          await processVerificationAward(GOLD_STAR_BADGE_LABEL, selectedRowData.id);
        }
      }

      dispatch(setShowRemediationValidationAlert(true));

      if (originalVerificationComment === verificationComment && originalVerificationStatus === verificationStatus) return;

      // Create audit log
      createAuditLog({
        user,
        method,
        event: LABELS.remediationValidation,
        description,
        directLink: `${window.location.origin}/assessment/${selectedRowData.assessment?.id}/questionnaire/${selectedRowData.bpSurvey}`,
        assessorAnswer: (currentSubRemediation.assessorAnswer as AssessorAnswer).id,
        questionAnswer: currentSubRemediation.questionAnswerId,
        assessment: selectedRowData.assessment?.id,
        assessmentSurvey: (selectedRowData.assessmentSurvey as number),
        companies: [selectedRowData.assessment.company as number],
      });

      if (!selectedRowData.assessment.id) return;
      // Create assessment notification
      createAssessmentNotification(
        selectedRowData.assessment.id,
        (selectedRowData.assessmentSurvey as number),
        parseInt((currentSubRemediation.answer as unknown as Answer).question),
        REMEDIATION_VALIDATION_STATUS_UPDATE,
        `Validation status changed to ${verificationStatus}`,
        false,
      );
    } catch (e) {
      console.error(e);
    }
  }, [selectedRowData, JWToken, handleFetchVerificationData, user, rows, dispatch]);

  useEffect(() => {
    getAuthSession().then((session) => {
      setJWToken(session.getIdToken().getJwtToken());
    });
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setBestPracticeList([]);
      return;
    }
  }, [searchTerm]);

  useEffect(() => {
    dispatch(fetchAll({ type: 'mpa_best_practice' }));
    fetchTPNAdminsList();
  }, []);

  useEffect(() => {
    if (openVerificationFlowDialog) return;

    handleFetchVerificationData(false, false, undefined, paginationModel.page);
  }, [paginationModel.page, paginationModel.pageSize, filterModelApplied, sortModel, openVerificationFlowDialog]);

  return (
    <>
      <Snackbar
        open={showSnackBar}
        autoHideDuration={5000}
        TransitionComponent={Slide}
        onClose={() => setShowSnackBar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSnackBar(false)} icon={<Done fontSize="inherit" />} severity="success" sx={{ width: '100%' }}>
          Best Practice Validation Updated
        </Alert>
      </Snackbar>
      <Card sx={{ padding: '2em' }}>
        <DataGridPro
          loading={loading}
          style={{ height: 400, width: '100%' }}
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          filterMode="server"
          sortingMode="server"
          rowCount={rowCount}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onSortModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSortModel(model);
          }}
          onPageChange={(newPage: number) => {
            setPaginationModel((prev) => ({ ...prev, page: newPage }));
          }}
          onPageSizeChange={(newPageSize: number) => {
            setPaginationModel(prev => ({ ...prev, pageSize: newPageSize, page: 0 })); // reset to first page
          }}
          filterModel={filterModelDraft}
          onFilterModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 })); // reset page like Users panel does
            setFilterModelDraft(model);

            const normalized = normalizeFilterModel(model);
            if (!sameModel(normalized, filterModelApplied)) {
              setFilterModelApplied(normalized);
            }
          }}

          rowsPerPageOptions={[10, 25]}
          getRowId={(row) => row.assessmentSurvey}
          components={{
            Toolbar: CustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              openFilterModal: handleOpenFilterModal,
              filterModelDraft,
              setFilterModelDraft,
              handleFetchVerificationData,
            },
          }}
          getRowHeight={() => 150}
          headerHeight={115}  
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'pre-line',
              lineHeight: '1.2',
              textAlign: 'left',
            },
          }}
        />
      </Card>
      <BestPracticeVerificationDialog
        isOpen={openFilterModal}
        handleClose={() => setOpenFilterModal(false)}
        questionnaires={questionnaires}
        questionnaireId={questionnaireId}
        setQuestionnaireId={setQuestionnaireId}
        controlList={controlList}
        handleFetchBestPracticeQuestions={handleFetchBestPracticeQuestions}
        handleBestPracticeChange={handleBestPracticeChange}
        handleDeleteBPVerification={handleDeleteBPVerification}
        bestPracticeList={bestPracticeList}
        setBestPracticeList={setBestPracticeList}
        handleInputChange={handleInputChange}
        isFetching={isFetching}
        autocompleteValue={autocompleteValue}
        setAutocompleteValue={setAutocompleteValue}
        loadingBP={loadingBP}
      />
      { selectedRowData && (
      <VerificationFlowDialog
        navigationLink={`/assessment/${selectedRowData.assessment?.id}/questionnaire/${selectedRowData.bpSurvey}`}
        badges={selectedRowData.badges as Badge[] || []}
        handleSaveVerificationStatus={handleSaveVerificationStatus}
        assessmentDate={selectedRowData.assessment?.dateAccepted || ''}
        companyName={selectedRowData.serviceProviderName || ''}
        remediationQuestionAnswers={
          [...(selectedRowData?.remediationQuestionAnswers || [])]
            .filter((item) => item.remediationPlan === REMEDIATION_OPTIONS.remediated)
        }
        isOpen={openVerificationFlowDialog}
        assignedTPNAdminReviewer={selectedRowData.assignedTpnAdminReviewer
          ? selectedRowData.assignedTpnAdminReviewer?.firstName + ' ' + selectedRowData.assignedTpnAdminReviewer?.lastName + ' - ' + selectedRowData.assignedTpnAdminReviewer?.email
          : ''
        }
        handleClose={() => setOpenVerificationFlowDialog(false)}
      />
      )}
      <Snackbar
        open={showRemediationValidationAlert}
        autoHideDuration={4000}
        TransitionComponent={Slide}
        onClose={() => dispatch(setShowRemediationValidationAlert(false))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => dispatch(setShowRemediationValidationAlert(false))} icon={<ThumbUp fontSize="inherit" />} severity="success" sx={{ width: '100%' }}>
          Remediation Verification Updated
        </Alert>
      </Snackbar>
      <Snackbar
        open={showTPNAdminAssignedAlert}
        autoHideDuration={4000}
        TransitionComponent={Slide}
        onClose={() => dispatch(setShowTPNAdminAssignedAlert(false))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => dispatch(setShowTPNAdminAssignedAlert(false))} icon={<ThumbUp fontSize="inherit" />} severity="success" sx={{ width: '100%' }}>
        TPN Admin Assigned
        </Alert>
      </Snackbar>
    </>
  );
}
